import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Founder / funder password recovery — consume step.
 *
 * Reads `?token=` from the URL (emailed magic link), POSTs it with the
 * new password. On success, redirects to /login with a small toast-like
 * confirmation.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ pw: "", pw2: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const tokenLooksValid = useMemo(() => token && token.length >= 20, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.pw.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    if (form.pw !== form.pw2) {
      setErr("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: form.pw,
      });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 1800);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F8F8]" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="mx-auto max-w-md px-4 py-12 sm:px-6 sm:py-16">
        <Link to="/" className="text-2xl font-extrabold text-[#0A8B85]">Clinq</Link>
        <h1 className="mt-6 text-2xl font-bold text-neutral-900 sm:mt-8 sm:text-3xl">
          Choose a new password
        </h1>

        {!tokenLooksValid ? (
          <div data-testid="reset-invalid-token" className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
            <p className="font-semibold">This reset link is invalid.</p>
            <p className="mt-1 text-red-800">
              The link may be broken or missing its token.{" "}
              <Link to="/forgot-password" className="font-semibold underline">
                Request a fresh one
              </Link>.
            </p>
          </div>
        ) : done ? (
          <div data-testid="reset-success" className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            <p className="font-semibold">Password updated.</p>
            <p className="mt-1 text-emerald-800">
              Taking you back to sign in…
            </p>
          </div>
        ) : (
          <>
            <p className="mt-3 text-sm text-neutral-600">
              Enter a new password — at least 8 characters.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input
                data-testid="reset-pw"
                type="password"
                placeholder="New password"
                required
                autoFocus
                value={form.pw}
                onChange={(e) => setForm({ ...form, pw: e.target.value })}
                className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#0A8B85]"
              />
              <input
                data-testid="reset-pw-confirm"
                type="password"
                placeholder="Confirm new password"
                required
                value={form.pw2}
                onChange={(e) => setForm({ ...form, pw2: e.target.value })}
                className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#0A8B85]"
              />
              {err && (
                <p data-testid="reset-error" className="text-sm text-red-600">
                  {String(err)}
                </p>
              )}
              <button
                data-testid="reset-submit"
                disabled={busy}
                type="submit"
                className="w-full rounded-lg bg-[#0A8B85] py-3 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50"
              >
                {busy ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}

        <p className="mt-6 text-center text-sm text-neutral-600">
          <Link to="/login" data-testid="reset-back-to-login"
                className="font-semibold text-[#0A8B85] hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
